/**
 * AgenteLead Search OS — Iter212 · Advanced Segment & Geo Search Builder.
 *
 *  ┌───────────────────────────────────────────────────────────────────┐
 *  │  Location (Country → State → City → Radius)                       │
 *  │  Segment  (8 packs · emoji · label)                               │
 *  │  Keywords chips (from pack.core + pack.operational + user-added)  │
 *  │  Precision (Wide / Balanced / Precise)                            │
 *  │  ICP Filters (min employees / DM titles / signals)                │
 *  │  Preview → Sub-Queries panel                                       │
 *  │  Run → Multi-Query Orchestrator                                    │
 *  └───────────────────────────────────────────────────────────────────┘
 *
 *  Falls back to natural-language query (legacy path) when the operator
 *  toggles "Modo livre".  Every discovered company keeps the same
 *  Confidence / Opportunity / Evidence UX from Phase 1.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Search, Globe, Building2, MapPin, Users,
  DollarSign, Zap, ShieldCheck, ShieldAlert, ShieldOff, AlertTriangle, ChevronRight,
  RefreshCw, Plus, ExternalLink, FileSearch, Activity, X, Layers,
  Filter, Target, Wand2, Info, Send,
  Download, ChevronDown, ChevronUp, FileText, FileSpreadsheet, FileType,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import { useClaireContext } from "../../hooks/useClaireContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

const TIER_COLORS = {
  "A+": { bg: "linear-gradient(135deg,#10B981 0%,#065F46 100%)", label: "A+ Strategic" },
  "A":  { bg: "linear-gradient(135deg,#10B981 0%,#047857 100%)", label: "A High Priority" },
  "B":  { bg: "linear-gradient(135deg,#3B82F6 0%,#1E3A8A 100%)", label: "B Qualified" },
  "C":  { bg: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)", label: "C Nurture" },
  "D":  { bg: "linear-gradient(135deg,#64748B 0%,#334155 100%)", label: "D Low Priority" },
};

const CONFIDENCE_COLORS = {
  VERY_HIGH: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  HIGH:      "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
  MEDIUM:    "text-sky-300 border-sky-500/30 bg-sky-500/5",
  LOW:       "text-amber-300 border-amber-500/30 bg-amber-500/5",
  VERY_LOW:  "text-rose-300 border-rose-500/40 bg-rose-500/10",
};

const PRECISION_OPTIONS = [
  { id: "wide",     label: "Wide",     hint: "3 queries · rápido",  color: "sky",     icon: Layers },
  { id: "balanced", label: "Balanced", hint: "5 queries · padrão",  color: "purple",  icon: Target },
  { id: "precise",  label: "Precise",  hint: "8 queries · exaustivo", color: "emerald", icon: ShieldCheck },
];

const DM_TITLE_SUGGESTIONS = [
  "Security Director", "VP of Security", "Chief Security Officer",
  "Head of Facilities", "Facilities Manager", "Operations Director",
  "COO", "General Manager",
];

const SIGNAL_SUGGESTIONS = [
  "expansion", "new construction", "leadership hire", "M&A",
  "opening new location", "RFP posted", "new facility permit",
];

export default function SmithAISearch() {
  const navigate = useNavigate();

  // ── Builder state ───────────────────────────────────────────────────
  const [country, setCountry]   = useState("US");
  const [state,   setStateVal]  = useState("FL");
  const [city,    setCity]      = useState("Orlando");
  const [radius,  setRadius]    = useState(75);
  const [segment, setSegment]   = useState("hospitals");
  const [precision, setPrecision] = useState("balanced");
  const [keywords, setKeywords] = useState([]);              // selected chips
  const [customKw, setCustomKw] = useState("");
  const [minEmployees, setMinEmployees] = useState(100);
  const [dmTitles, setDmTitles] = useState([]);
  const [signals,  setSignals]  = useState([]);

  // ── Ref data ────────────────────────────────────────────────────────
  const [countries, setCountries] = useState([]);
  const [states,    setStates]    = useState([]);
  const [cities,    setCities]    = useState([]);
  const [segments,  setSegments]  = useState([]);
  const [segmentPack, setSegmentPack] = useState(null);

  // ── Run state ───────────────────────────────────────────────────────
  const [previewing, setPreviewing] = useState(false);
  const [searching,  setSearching]  = useState(false);
  const [subQueries, setSubQueries] = useState([]);
  const [companies,  setCompanies]  = useState([]);
  const [lastRun,    setLastRun]    = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);
  const [savingId,   setSavingId]   = useState(null);
  const [evidenceOpen, setEvidenceOpen] = useState(null);
  const [evidenceRows, setEvidenceRows] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);   // Iter225 · collapsed by default to save vertical space
  const [recentRuns, setRecentRuns] = useState([]);
  const [autoSendOpen, setAutoSendOpen] = useState(false);

  // Iter326 · Publish AI Search context to Claire so she can quote
  // the exact filters and top result without guessing.
  useClaireContext({
    page: "ai-lead-search",
    filters: {
      country, state, city, radius_mi: radius, segment,
      precision, min_employees: minEmployees,
      keywords: keywords?.slice(0, 6) || [],
    },
    visible_count: companies.length,
    total_count: companies.length,
    top_company: companies[0]
      ? { id: companies[0].id, name: companies[0].name,
           domain: companies[0].domain || companies[0].website }
      : null,
    project: "Smith Protective",
  });

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }),
    []
  );

  // ── Bootstrap ───────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          axios.get(`${API}/ai-search/geo/countries`, authHeader),
          axios.get(`${API}/ai-search/segments`,      authHeader),
        ]);
        setCountries(c.data?.countries || []);
        setSegments(s.data?.segments || []);
      } catch { /* silent */ }
      loadRecentRuns();
      loadSavedCompanies();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/ai-search/geo/states?country=${country}`,
          authHeader);
        setStates(r.data?.states || []);
      } catch { setStates([]); }
    })();
  }, [country, authHeader]);

  useEffect(() => {
    (async () => {
      if (!state) { setCities([]); return; }
      try {
        const r = await axios.get(
          `${API}/ai-search/geo/cities?country=${country}&state=${state}`,
          authHeader);
        setCities(r.data?.cities || []);
      } catch { setCities([]); }
    })();
  }, [country, state, authHeader]);

  useEffect(() => {
    (async () => {
      if (!segment) { setSegmentPack(null); return; }
      try {
        const r = await axios.get(`${API}/ai-search/segments/${segment}`, authHeader);
        setSegmentPack(r.data?.segment || null);
        // Auto-seed keywords with the core + operational lists (top 6).
        const seed = [
          ...(r.data?.segment?.core || []).slice(0, 3),
          ...(r.data?.segment?.operational || []).slice(0, 3),
        ];
        setKeywords(seed);
      } catch { setSegmentPack(null); }
    })();
  }, [segment, authHeader]);

  const loadRecentRuns = async () => {
    try {
      const r = await axios.get(`${API}/ai-search/runs?limit=10`, authHeader);
      setRecentRuns(r.data?.runs || []);
    } catch { /* silent */ }
  };
  const loadSavedCompanies = async () => {
    try {
      const r = await axios.get(`${API}/ai-search/companies?limit=30`, authHeader);
      setCompanies((prev) => prev.length ? prev : (r.data?.companies || []));
    } catch { /* silent */ }
  };

  const currentCountry = countries.find((c) => c.code === country);
  const radiusOptions = currentCountry?.radius_options || [10, 25, 50, 75, 100, 150];
  const unit = currentCountry?.unit || "miles";

  const toggleKeyword = (kw) => {
    setKeywords((prev) =>
      prev.includes(kw) ? prev.filter((k) => k !== kw) : [...prev, kw]
    );
  };
  const addCustomKw = () => {
    const kw = customKw.trim();
    if (!kw) return;
    if (!keywords.includes(kw)) setKeywords([...keywords, kw]);
    setCustomKw("");
  };
  const removeKw = (kw) => setKeywords(keywords.filter((k) => k !== kw));

  const toggleFromList = (list, setList, item) => {
    setList(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  };

  const buildPayload = () => ({
    country, state, city: city || null, radius,
    segment, keywords_selected: keywords, precision,
    filters: {
      min_employees: minEmployees || null,
      decision_maker_titles: dmTitles.length ? dmTitles : null,
      buying_signals: signals.length ? signals : null,
    },
    language: currentCountry?.language || "en",
  });

  const handlePreview = async () => {
    setPreviewing(true);
    setSubQueries([]);
    try {
      const r = await axios.post(`${API}/ai-search/preview-builder`,
        buildPayload(), authHeader);
      setSubQueries(r.data?.sub_queries || []);
      if (r.data?.sub_queries?.length) {
        toast.success(`${r.data.sub_queries.length} sub-queries prontas`);
      }
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao gerar preview");
    } finally { setPreviewing(false); }
  };

  const handleRun = async () => {
    if (!segment || !country) {
      toast.error("Escolha país + segmento primeiro");
      return;
    }
    setSearching(true);
    setCompanies([]);
    setLastRun(null);
    try {
      const r = await axios.post(`${API}/ai-search/builder`,
        buildPayload(),
        { ...authHeader, timeout: 300000 });
      setCompanies(r.data?.companies || []);
      setLastRun({
        run_id:     r.data?.run_id,
        status:     r.data?.status,
        cost:       r.data?.cost,
        total_evidence:  r.data?.total_evidence,
        total_conflicts: r.data?.total_conflicts,
        error:      r.data?.error,
      });
      if (r.data?.error) {
        toast.error(`Busca com erros: ${r.data.error}`);
      } else if (!r.data?.companies?.length) {
        toast.info("Nenhuma empresa retornada — amplie os keywords ou o raio");
      } else if (r.data.companies.length < 30) {
        toast.warning(
          `${r.data.companies.length} empresas retornadas (meta ≥ 30). ` +
          `Expansão automática já rodou — tente ampliar raio, sinônimos ou cidade vizinha.`);
      } else {
        toast.success(`${r.data.companies.length} empresas descobertas`);
      }
      loadRecentRuns();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha na busca AI");
    } finally { setSearching(false); }
  };

  const handleEnrich = async (companyId) => {
    setEnrichingId(companyId);
    try {
      const r = await axios.post(`${API}/ai-search/companies/${companyId}/enrich`,
        {}, { ...authHeader, timeout: 180000 });
      if (r.data?.ok) {
        toast.success("Enriquecimento concluído");
        const list = await axios.get(`${API}/ai-search/companies?limit=30`, authHeader);
        setCompanies(list.data?.companies || []);
      } else toast.error(r.data?.error || "Enriquecimento falhou");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Enriquecimento falhou");
    } finally { setEnrichingId(null); }
  };

  const handleAddToCrm = async (companyId) => {
    setSavingId(companyId);
    try {
      const r = await axios.post(`${API}/ai-search/companies/${companyId}/add-to-crm`,
        { segment }, authHeader);
      const pid = r.data?.prospect_id;
      const dup = r.data?.duplicate;
      // Mark card as in-CRM locally so the badge appears instantly.
      setCompanies((prev) => prev.map((c) =>
        c.id === companyId ? { ...c, in_crm: true, crm_prospect_id: pid } : c));
      if (dup) toast.info("Já existe no CRM · abra no Kanban para ver");
      else toast.success("Adicionado ao CRM (Smith Prospects)");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao salvar no CRM");
    } finally { setSavingId(null); }
  };

  const openEvidence = async (companyId) => {
    setEvidenceOpen(companyId);
    setEvidenceRows([]);
    try {
      const r = await axios.get(`${API}/ai-search/companies/${companyId}/evidence`,
        authHeader);
      setEvidenceRows(r.data?.evidence || []);
    } catch { toast.error("Falha ao carregar evidências"); }
  };

  // Keyword-pack chip groups
  const kwGroups = useMemo(() => {
    if (!segmentPack) return [];
    return [
      { key: "core",         label: "Core",         items: segmentPack.core         || [] },
      { key: "operational",  label: "Operational",  items: segmentPack.operational  || [] },
      { key: "security",     label: "Security",     items: segmentPack.security     || [] },
      { key: "commercial",   label: "Commercial",   items: segmentPack.commercial   || [] },
    ];
  }, [segmentPack]);

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-ai-search-header"
          title="AI Lead Search Builder"
          subtitle="Cascading Geo · Segment Intelligence · Multi-Query Orchestrator"
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#7C3AED 0%,#3B0764 100%)", color: "#FDE047" }}>
              <Sparkles className="w-5 h-5" />
            </div>
          }
        >
          <button type="button" onClick={() => setAutoSendOpen(true)}
            data-testid="ai-search-open-autosend-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-white hover:border-amber-500/60 inline-flex items-center gap-1.5"
            title="Configurar Auto-Send de drafts com alta pontuação">
            <Send className="w-3.5 h-3.5" /> Auto-Send
          </button>
          <button type="button" onClick={() => navigate("/smith/ai-search/settings")}
            data-testid="ai-search-open-settings-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-white hover:border-cyan-500/60 inline-flex items-center gap-1.5"
            title="Configurar toggles de scrapers legados">
            <ShieldOff className="w-3.5 h-3.5" /> Scraper Retirement
          </button>
        </AppHeader>

        <main className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 space-y-6">

          {/* ─── Anti-hallucination banner ───────────────────────── */}
          <div data-testid="ai-search-verified-banner"
            className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-[11.5px] text-amber-200 flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <strong>Evidence-first search.</strong> Toda descoberta é validada
              em fontes públicas (site oficial, imprensa, licitações). Campos
              sem prova aparecem como <em>"Não verificado"</em> — nunca inventados.
            </div>
          </div>

          {/* ─── Iter225/226 · Turbo mode banner (mandatório) ────── */}
          <div data-testid="ai-search-turbo-banner"
            className="rounded-lg border border-red-500/40 p-3 flex items-center gap-3 flex-wrap"
            style={{ background: "linear-gradient(90deg, rgba(220,38,38,0.14), rgba(127,29,29,0.06))" }}>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-extrabold px-2 py-1 rounded-full text-white shrink-0"
                  style={{ background: "linear-gradient(135deg,#DC2626,#7F1D1D)" }}>
              <Zap className="w-3 h-3" /> Turbo ON · min. 30
            </span>
            <div className="text-[11.5px] text-slate-100 flex-1">
              <strong>Modo Turbo obrigatório + Volume mínimo garantido.</strong> Todas
              as queries usam web-search em profundidade máxima
              (<code>search_context_size="high"</code>) e a busca só para quando
              atinge <b>≥ 30 empresas únicas por segmento</b>. Se o volume
              inicial for menor, o orquestrador expande automaticamente o raio,
              a região metropolitana e cruza sinônimos do segmento — sem
              fallback raso.
            </div>
          </div>

          {/* ─── Location ────────────────────────────────────────── */}
          <section data-testid="ai-search-section-location"
            className="rounded-2xl border border-white/10 p-4 space-y-3"
            style={{ background: "linear-gradient(135deg,rgba(59,130,246,0.06) 0%,rgba(15,23,42,0.4) 100%)" }}>
            <SectionHeader icon={MapPin} color="#3B82F6" label="Location"
              hint="Country → State → City → Radius" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Field label="Country">
                <select value={country} onChange={(e) => { setCountry(e.target.value); setStateVal(""); setCity(""); }}
                  data-testid="ai-search-country"
                  className="w-full h-9 px-2 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white">
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="State/UF">
                <select value={state} onChange={(e) => { setStateVal(e.target.value); setCity(""); }}
                  data-testid="ai-search-state"
                  className="w-full h-9 px-2 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white">
                  <option value="">— any —</option>
                  {states.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="City">
                <select value={city} onChange={(e) => setCity(e.target.value)}
                  data-testid="ai-search-city"
                  className="w-full h-9 px-2 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white"
                  disabled={!state}>
                  <option value="">— any —</option>
                  {cities.map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label={`Radius (${unit})`}>
                <select value={radius} onChange={(e) => setRadius(parseInt(e.target.value, 10))}
                  data-testid="ai-search-radius"
                  className="w-full h-9 px-2 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white"
                  disabled={!city}>
                  {radiusOptions.map((r) => (
                    <option key={r} value={r}>{r} {unit}</option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          {/* ─── Segment + Keywords ─────────────────────────────── */}
          <section data-testid="ai-search-section-segment"
            className="rounded-2xl border border-white/10 p-4 space-y-3"
            style={{ background: "linear-gradient(135deg,rgba(124,58,237,0.06) 0%,rgba(15,23,42,0.4) 100%)" }}>
            <SectionHeader icon={Layers} color="#7C3AED" label="Segment Intelligence"
              hint="Pick a pack — keywords auto-populated. Add/remove as needed." />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {segments.map((s) => (
                <button key={s.id} type="button"
                  data-testid={`ai-search-segment-${s.id}`}
                  onClick={() => setSegment(s.id)}
                  className={`h-11 px-2 rounded-lg border text-[12.5px] font-bold text-white inline-flex items-center gap-1.5 transition-colors ${
                    segment === s.id
                      ? "border-purple-500 bg-purple-500/15"
                      : "border-white/15 hover:border-purple-500/50"}`}>
                  <span>{s.emoji}</span>
                  <span className="truncate">{s.label}</span>
                </button>
              ))}
            </div>

            {segmentPack && (
              <div className="space-y-2 pt-2 border-t border-white/5">
                <div className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold">
                  Selected keywords ({keywords.length})
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="ai-search-selected-keywords">
                  {keywords.length === 0 && (
                    <span className="text-[11px] text-slate-500 italic">— nenhum ainda —</span>
                  )}
                  {keywords.map((kw) => (
                    <span key={kw}
                      className="text-[11px] px-2 py-1 rounded-full border border-purple-500/40 bg-purple-500/10 text-purple-100 inline-flex items-center gap-1">
                      {kw}
                      <button type="button" onClick={() => removeKw(kw)}
                        data-testid={`ai-search-remove-kw-${kw}`}
                        className="opacity-70 hover:opacity-100">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input value={customKw} onChange={(e) => setCustomKw(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomKw())}
                    placeholder="Adicionar palavra-chave customizada…"
                    data-testid="ai-search-custom-kw-input"
                    className="flex-1 h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[12.5px] text-white placeholder:text-slate-500 focus:border-purple-500/60 focus:outline-none" />
                  <button type="button" onClick={addCustomKw}
                    data-testid="ai-search-custom-kw-add"
                    className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-white hover:border-purple-500/60 inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>

                {kwGroups.map((g) => g.items?.length > 0 && (
                  <div key={g.key} className="space-y-1">
                    <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">
                      {g.label} pack ({g.items.length})
                    </div>
                    <div className="flex flex-wrap gap-1"
                      data-testid={`ai-search-kwgroup-${g.key}`}>
                      {g.items.map((kw) => (
                        <button key={kw} type="button"
                          onClick={() => toggleKeyword(kw)}
                          data-testid={`ai-search-kw-${g.key}-${kw.replace(/\s+/g, "-").slice(0, 30)}`}
                          className={`text-[10.5px] px-2 h-6 rounded-full border inline-flex items-center gap-1 transition-colors ${
                            keywords.includes(kw)
                              ? "border-purple-500 bg-purple-500/15 text-purple-100"
                              : "border-white/15 text-slate-300 hover:border-purple-500/40"}`}>
                          {keywords.includes(kw) && <ShieldCheck className="w-2.5 h-2.5" />}
                          {kw}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ─── Precision + ICP Filters ────────────────────────── */}
          <section data-testid="ai-search-section-precision"
            className="rounded-2xl border border-white/10 p-4 space-y-3">
            <SectionHeader icon={Target} color="#F59E0B" label="Precision & ICP"
              hint="Wide (fast) → Precise (exhaustive). ICP filters narrow the LLM." />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {PRECISION_OPTIONS.map((p) => {
                const Icon = p.icon;
                const active = precision === p.id;
                return (
                  <button key={p.id} type="button"
                    onClick={() => setPrecision(p.id)}
                    data-testid={`ai-search-precision-${p.id}`}
                    className={`p-3 rounded-lg border text-left transition-colors ${
                      active ? `border-${p.color}-500 bg-${p.color}-500/10` : "border-white/15 hover:border-white/30"}`}
                    style={active ? {
                      borderColor: p.color === "sky" ? "#0EA5E9"
                        : p.color === "purple" ? "#8B5CF6" : "#10B981",
                      background: p.color === "sky" ? "rgba(14,165,233,0.10)"
                        : p.color === "purple" ? "rgba(139,92,246,0.10)"
                        : "rgba(16,185,129,0.10)",
                    } : {}}>
                    <div className="flex items-center gap-2 text-white font-bold text-[13px]">
                      <Icon className="w-4 h-4" /> {p.label}
                    </div>
                    <div className="text-[10.5px] text-slate-400 mt-0.5">{p.hint}</div>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-white/5">
              <Field label="Min. employees">
                <input type="number" min="0" step="10"
                  data-testid="ai-search-min-employees"
                  value={minEmployees || ""} onChange={(e) => setMinEmployees(parseInt(e.target.value || "0", 10))}
                  className="w-full h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white" />
              </Field>
              <Field label="Decision-maker titles">
                <ChipCombobox
                  testidPrefix="ai-search-dm-title"
                  values={dmTitles} suggestions={DM_TITLE_SUGGESTIONS}
                  onAdd={(v) => toggleFromList(dmTitles, setDmTitles, v)}
                  onRemove={(v) => toggleFromList(dmTitles, setDmTitles, v)} />
              </Field>
              <Field label="Buying signals hint">
                <ChipCombobox
                  testidPrefix="ai-search-signal"
                  values={signals} suggestions={SIGNAL_SUGGESTIONS}
                  onAdd={(v) => toggleFromList(signals, setSignals, v)}
                  onRemove={(v) => toggleFromList(signals, setSignals, v)} />
              </Field>
            </div>
          </section>

          {/* ─── Sub-Query Preview + Run ────────────────────────── */}
          <section data-testid="ai-search-section-preview"
            className="rounded-2xl border border-white/10 p-4 space-y-3"
            style={{ background: "linear-gradient(135deg,rgba(16,185,129,0.04) 0%,rgba(15,23,42,0.4) 100%)" }}>
            <SectionHeader icon={Wand2} color="#10B981" label="Query Orchestration"
              hint="Preview the sub-queries the AI will fire — decoupled from web spend." />
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={handlePreview} disabled={previewing}
                data-testid="ai-search-preview-btn"
                className="h-9 px-3 rounded-lg border border-white/20 text-[12px] font-bold text-white hover:border-emerald-500/60 inline-flex items-center gap-1.5 disabled:opacity-50">
                {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSearch className="w-3.5 h-3.5" />}
                Preview sub-queries
              </button>
              <button type="button" onClick={handleRun} disabled={searching || previewing}
                data-testid="ai-search-run-btn"
                className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50 ml-auto"
                style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
                {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                {searching ? "Pesquisando…" : "Rodar AI Search"}
              </button>
            </div>

            {subQueries.length > 0 && (
              <div className="space-y-1.5" data-testid="ai-search-subqueries">
                {subQueries.map((sq) => (
                  <div key={sq.index}
                    className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2.5 flex items-start gap-2">
                    <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold shrink-0 mt-0.5">
                      Q{sq.index} · {sq.purpose}
                    </span>
                    <div className="text-[12px] text-slate-200">{sq.query}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ─── Cost snapshot ─────────────────────────────────── */}
          {lastRun && (
            <section data-testid="ai-search-cost-snapshot"
              className="rounded-2xl border border-white/10 p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              <CostTile icon={Activity} label="Run" v={(lastRun.run_id || "—").slice(0, 8)} />
              <CostTile icon={Search} label="Web calls"
                v={`${lastRun.cost?.web_calls_used || 0} / ${lastRun.cost?.web_calls_max || 0}`} />
              <CostTile icon={DollarSign} label="Custo estim."
                v={`$${lastRun.cost?.cost_used_usd || "0.000000"}`} />
              <CostTile icon={ShieldCheck} label="Evidências" v={lastRun.total_evidence || 0} />
              <CostTile icon={AlertTriangle} label="Conflitos" v={lastRun.total_conflicts || 0}
                accent={(lastRun.total_conflicts || 0) > 0 ? "amber" : null} />
            </section>
          )}

          {/* ─── Results ───────────────────────────────────────── */}
          <section data-testid="ai-search-results">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-[13px] uppercase tracking-widest font-bold text-slate-300">
                {companies.length ? `${companies.length} empresas descobertas` : "Empresas salvas"}
              </h2>
              <div className="flex items-center gap-2">
                {/* Iter225 · Export bar */}
                <ExportBar runId={lastRun?.run_id} disabled={companies.length === 0}
                           companies={companies} />
                <button type="button" onClick={loadSavedCompanies}
                  data-testid="ai-search-refresh-companies"
                  className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Recarregar
                </button>
              </div>
            </div>

            {searching && (
              <div className="rounded-2xl border border-white/10 p-8 text-center text-[13px] text-slate-400 flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                <div>Multi-Query Orchestrator ativo — {subQueries.length || precision} queries em execução</div>
                <div className="text-[10.5px] text-slate-500">Pode levar 1-3 minutos. Não feche a página.</div>
              </div>
            )}

            {!searching && companies.length === 0 && (
              <div className="rounded-2xl border border-white/10 p-8 text-center text-[12.5px] text-slate-400">
                Nenhuma empresa ainda. Configure a busca acima e clique em <em>Rodar AI Search</em>.
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {companies.map((c) => (
                <CompanyCard key={c.id} c={c}
                  onOpen={() => navigate(`/smith/ai-search/company/${c.id}`)}
                  onEnrich={() => handleEnrich(c.id)}
                  enriching={enrichingId === c.id}
                  onAddToCrm={() => handleAddToCrm(c.id)}
                  saving={savingId === c.id}
                  onEvidence={() => openEvidence(c.id)} />
              ))}
            </div>
          </section>

          {/* ─── Recent runs (Iter225 · collapsible) ─────────────── */}
          {recentRuns.length > 0 && (
            <section data-testid="ai-search-recent-runs">
              <button type="button" onClick={() => setHistoryOpen((v) => !v)}
                data-testid="ai-search-recent-runs-toggle"
                className="w-full mb-3 flex items-center gap-2 text-[13px] uppercase tracking-widest font-bold text-slate-300 hover:text-white transition-colors">
                <Activity className="w-3.5 h-3.5" />
                Histórico de pesquisas
                <span className="text-[10.5px] text-slate-500 normal-case tracking-normal font-normal">
                  · {recentRuns.length} recentes
                </span>
                <span className="ml-auto text-slate-500">
                  {historyOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
              </button>
              {historyOpen && (
                <div className="rounded-2xl border border-white/10 divide-y divide-white/5"
                     data-testid="ai-search-recent-runs-list">
                  {recentRuns.slice(0, 6).map((r) => (
                    <div key={r.id} className="p-3 flex items-center gap-3 text-[12px]">
                      <div className={`h-6 px-2 rounded text-[10px] font-bold inline-flex items-center ${
                        r.status === "COMPLETED" ? "bg-emerald-500/15 text-emerald-300"
                          : r.status === "FAILED" ? "bg-rose-500/15 text-rose-300"
                          : "bg-slate-500/15 text-slate-300"}`}>
                        {r.status}
                      </div>
                      <div className="flex-1 truncate text-slate-300">
                        {r.intent?.normalized_query || r.company_id || "—"}
                      </div>
                      <div className="text-[10.5px] text-slate-500 tabular-nums">
                        {r.result_count || 0} • ${r.budget_snapshot?.cost_used_usd || "0.000000"}
                        {r.cache_hit && <span className="ml-2 text-emerald-400">cache</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* Evidence drawer */}
      {evidenceOpen && (
        <EvidenceDrawer rows={evidenceRows}
          onClose={() => setEvidenceOpen(null)} />
      )}

      {/* Auto-Send modal */}
      {autoSendOpen && (
        <AutoSendModal onClose={() => setAutoSendOpen(false)} authHeader={authHeader} />
      )}
    </div>
  );
}

// ── Sub components ──────────────────────────────────────────────────────

function SectionHeader({ icon: Icon, color, label, hint }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
           style={{ background: `${color}22`, color }}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div>
        <div className="text-[13px] font-bold text-white">{label}</div>
        <div className="text-[10.5px] text-slate-400">{hint}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}

function ChipCombobox({ values, suggestions, onAdd, onRemove, testidPrefix }) {
  const [input, setInput] = useState("");
  const commit = () => {
    const v = input.trim();
    if (v && !values.includes(v)) onAdd(v);
    setInput("");
  };
  return (
    <div className="rounded-lg border border-white/15 bg-black/30 p-2 space-y-1.5"
         data-testid={`${testidPrefix}-container`}>
      <div className="flex flex-wrap gap-1">
        {values.map((v) => (
          <span key={v}
            className="text-[10.5px] px-1.5 py-0.5 rounded border border-purple-500/40 bg-purple-500/10 text-purple-100 inline-flex items-center gap-1">
            {v}
            <button type="button" onClick={() => onRemove(v)}
              data-testid={`${testidPrefix}-remove-${v.replace(/\s+/g, "-").slice(0, 24)}`}
              className="opacity-70 hover:opacity-100">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
      </div>
      <input value={input} onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), commit())}
        onBlur={commit} placeholder="Digite e Enter…"
        data-testid={`${testidPrefix}-input`}
        className="w-full h-6 px-1 bg-transparent text-[12px] text-white placeholder:text-slate-500 focus:outline-none" />
      <div className="flex flex-wrap gap-1 pt-1 border-t border-white/5">
        {suggestions.slice(0, 6).map((s) => (
          <button key={s} type="button" onClick={() => onAdd(s)}
            data-testid={`${testidPrefix}-sugg-${s.replace(/\s+/g, "-").slice(0, 24)}`}
            className={`text-[10px] px-1.5 h-5 rounded border ${values.includes(s)
              ? "border-purple-500/50 text-purple-100 bg-purple-500/10"
              : "border-white/10 text-slate-400 hover:border-purple-500/40"}`}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function CostTile({ icon: Icon, label, v, accent }) {
  const accentCls = accent === "amber" ? "text-amber-300" : "text-slate-200";
  return (
    <div className="rounded-lg border border-white/10 p-3 flex items-center gap-2.5">
      <Icon className={`w-4 h-4 ${accentCls}`} />
      <div>
        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className={`text-[13px] font-bold ${accentCls}`}>{v}</div>
      </div>
    </div>
  );
}

function CompanyCard({ c, onOpen, onEnrich, enriching, onAddToCrm, saving, onEvidence }) {
  const tier = TIER_COLORS[c.opportunity_tier] || TIER_COLORS["D"];
  const confCls = CONFIDENCE_COLORS[c.confidence_band] || CONFIDENCE_COLORS.LOW;
  const inCrm = !!c.in_crm;
  const kanbanUrl = c.crm_prospect_id
    ? `/smith/kanban?highlight=${c.crm_prospect_id}` : null;
  // Iter225 · Collapsible buying-signals block (opportunity_reasons +
  // buying_signals). Start closed on cards past the first render pass
  // so long lists stay scannable.
  const [signalsOpen, setSignalsOpen] = useState(true);
  const signalCount = (c.opportunity_reasons?.length || 0)
                       + (c.buying_signals?.length || 0);
  return (
    <div data-testid={`ai-search-company-${c.id}`}
      className={`rounded-2xl border p-4 space-y-3 transition-all bg-white dark:bg-transparent shadow-sm dark:shadow-none ${
        inCrm ? "border-emerald-500/40 hover:border-emerald-500/70 hover:shadow-md"
              : "border-slate-200 dark:border-white/10 hover:border-purple-500/40 hover:shadow-md"}`}>
      <header className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: tier.bg, color: "white" }}>
          <Building2 className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <button type="button" onClick={onOpen}
            data-testid={`ai-search-open-company-${c.id}`}
            className="text-left group w-full">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[14px] font-bold text-slate-900 dark:text-white truncate group-hover:text-purple-600 dark:group-hover:text-purple-300">{c.name}</div>
              <span className="text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold text-white shrink-0"
                    style={{ background: tier.bg }}
                    data-testid={`ai-search-tier-${c.id}`}>
                {c.opportunity_tier} · {c.opportunity_score}
              </span>
              {inCrm && (
                <span className="text-[9.5px] uppercase tracking-widest px-1.5 py-0.5 rounded font-bold text-white shrink-0 inline-flex items-center gap-1"
                      style={{ background: "linear-gradient(135deg,#10B981 0%,#047857 100%)" }}
                      data-testid={`ai-search-in-crm-${c.id}`}>
                  <ShieldCheck className="w-2.5 h-2.5" /> In CRM
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2 flex-wrap">
              {c.industry && <span>{c.industry}</span>}
              {c.headquarters?.city && <><span>·</span><span>{c.headquarters.city}, {c.headquarters.state}</span></>}
              {c.employee_count_min && <><span>·</span><span>{c.employee_count_min}{c.employee_count_max && c.employee_count_max !== c.employee_count_min ? `–${c.employee_count_max}` : ""} empl.</span></>}
            </div>
          </button>
        </div>
      </header>

      {signalCount > 0 && (
        <div data-testid={`ai-search-signals-${c.id}`}
             className="rounded border border-white/5 bg-white/[0.015]">
          <button type="button" onClick={() => setSignalsOpen((v) => !v)}
            data-testid={`ai-search-signals-toggle-${c.id}`}
            className="w-full px-2 py-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-300 hover:text-white transition-colors">
            <Target className="w-3 h-3" />
            Buying signals ({signalCount})
            <span className="ml-auto text-slate-500">
              {signalsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </span>
          </button>
          {signalsOpen && (
            <ul className="text-[11.5px] text-slate-300 space-y-0.5 px-3 pb-2">
              {(c.opportunity_reasons || []).slice(0, 5).map((r, i) => (
                <li key={`r${i}`} className="flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" /> {r}
                </li>
              ))}
              {(c.buying_signals || []).slice(0, 3).map((s, i) => (
                <li key={`s${i}`} className="flex items-start gap-1.5">
                  <Zap className="w-3 h-3 mt-0.5 text-amber-400 shrink-0" />
                  {typeof s === "string" ? s
                    : s.headline || s.title || s.signal_type || "—"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold ${confCls}`}
              data-testid={`ai-search-confidence-${c.id}`}>
          {c.confidence_band?.replace("_", " ")} · {c.confidence_overall}
        </span>
        <button type="button" onClick={onEvidence}
          data-testid={`ai-search-evidence-btn-${c.id}`}
          className="text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-slate-300 hover:border-purple-500/50 hover:text-white inline-flex items-center gap-1">
          <FileSearch className="w-2.5 h-2.5" /> {c.evidence_count || 0} evidências
        </button>
        {c.unresolved_fields?.length > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 bg-amber-500/10 inline-flex items-center gap-1"
                title={`Não verificado: ${c.unresolved_fields.join(", ")}`}>
            <AlertTriangle className="w-2.5 h-2.5" /> {c.unresolved_fields.length} não verificados
          </span>
        )}
        {c.website && (
          <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer"
             className="text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-slate-300 hover:border-purple-500/50 hover:text-white inline-flex items-center gap-1">
            <Globe className="w-2.5 h-2.5" /> Site
          </a>
        )}
      </div>

      <div className="flex gap-2 pt-1 flex-wrap">
        <button type="button" onClick={onOpen}
          data-testid={`ai-search-dossier-btn-${c.id}`}
          className="h-8 px-2.5 rounded-lg text-[11px] font-bold text-white inline-flex items-center gap-1.5"
          style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
          <Sparkles className="w-3 h-3" /> Open Dossier
        </button>
        <button type="button" onClick={onEnrich} disabled={enriching}
          data-testid={`ai-search-enrich-btn-${c.id}`}
          className="h-8 px-2.5 rounded-lg border border-white/15 text-[11px] font-bold text-white hover:border-purple-500/60 inline-flex items-center gap-1.5 disabled:opacity-50">
          {enriching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Enrich
        </button>
        {inCrm && kanbanUrl ? (
          <a href={kanbanUrl}
            data-testid={`ai-search-view-kanban-btn-${c.id}`}
            className="h-8 px-2.5 rounded-lg text-[11px] font-bold text-white inline-flex items-center gap-1.5 ml-auto"
            style={{ background: "linear-gradient(135deg,#10B981 0%,#047857 100%)" }}>
            <ChevronRight className="w-3 h-3" /> Ver no Kanban
          </a>
        ) : (
          <button type="button" onClick={onAddToCrm} disabled={saving}
            data-testid={`ai-search-add-crm-btn-${c.id}`}
            className="h-8 px-2.5 rounded-lg text-[11px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50 ml-auto"
            style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add to CRM
          </button>
        )}
      </div>
    </div>
  );
}

function EvidenceDrawer({ rows, onClose }) {
  // Iter225 · Group evidence by field_name so the drawer stays scannable
  // even with 20+ items. Each group is collapsible.
  const groups = useMemo(() => {
    const g = new Map();
    (rows || []).forEach((ev) => {
      const key = ev.field_name || "misc";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(ev);
    });
    return Array.from(g.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [rows]);
  const [openGroups, setOpenGroups] = useState(() => new Set([groups[0]?.[0]]));
  const toggle = (k) => setOpenGroups((cur) => {
    const nxt = new Set(cur);
    nxt.has(k) ? nxt.delete(k) : nxt.add(k);
    return nxt;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center lg:justify-end"
         onClick={onClose} data-testid="ai-search-evidence-drawer">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-full lg:w-[560px] max-h-[85vh] overflow-y-auto rounded-t-2xl lg:rounded-none lg:rounded-l-2xl border-t lg:border-l border-white/10 p-5 space-y-3"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileSearch className="w-4 h-4 text-purple-400" />
            <h3 className="text-[14px] font-bold text-white">Evidências ({rows.length})</h3>
          </div>
          <button type="button" onClick={onClose}
            data-testid="ai-search-evidence-close"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {rows.length === 0 ? (
          <div className="text-[12px] text-slate-500 py-4">Nenhuma evidência registrada ainda.</div>
        ) : groups.map(([field, evs]) => (
          <div key={field}
               data-testid={`ai-search-evidence-group-${field}`}
               className="rounded-lg border border-white/10 overflow-hidden">
            <button type="button" onClick={() => toggle(field)}
              data-testid={`ai-search-evidence-group-toggle-${field}`}
              className="w-full px-3 py-2 flex items-center gap-2 text-[10.5px] uppercase tracking-widest hover:bg-white/5">
              <span className="text-purple-300 font-bold">{field}</span>
              <span className="text-slate-500 normal-case tracking-normal">· {evs.length} evidência{evs.length === 1 ? "" : "s"}</span>
              <span className="ml-auto text-slate-400">
                {openGroups.has(field) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </span>
            </button>
            {openGroups.has(field) && (
              <div className="px-3 pb-3 space-y-2 border-t border-white/5">
                {evs.map((ev) => (
                  <div key={ev.id || (ev.field_name + ev.source_url)}
                       className="rounded border border-white/5 bg-black/20 p-2.5 space-y-1">
                    <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">
                      {ev.source_type}
                    </div>
                    <div className="text-[12.5px] text-white break-words">{ev.claimed_value || "—"}</div>
                    {ev.excerpt && <div className="text-[11px] italic text-slate-400 line-clamp-3">"{ev.excerpt}"</div>}
                    <a href={ev.source_url} target="_blank" rel="noreferrer"
                       className="text-[11px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 break-all">
                      <ExternalLink className="w-3 h-3" /> {ev.source_title || ev.source_url}
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Iter225/226/227 · Multi-format export bar + Maps route ──────────
function ExportBar({ runId, disabled, companies = [] }) {
  const [busy, setBusy] = useState(null);   // fmt currently downloading
  const [open, setOpen] = useState(false);

  // Iter227 · One-click Google Maps route optimization.
  // Google Maps `/dir/` URL accepts up to 10 waypoints (including origin+dest).
  // We pick the top 10 companies with an address/city and build the URL.
  const buildMapsRoute = () => {
    const addressed = companies
      .filter((c) => (c.headquarters?.address || c.headquarters?.city || c.address || c.city))
      .slice(0, 10);
    if (addressed.length === 0) {
      toast.error("Nenhum lead com endereço para roteirizar");
      return;
    }
    const toWaypoint = (c) => {
      const hq = c.headquarters || {};
      const parts = [hq.address, hq.city, hq.state, hq.postal_code || hq.country || "US"]
        .filter(Boolean).join(", ");
      return parts || c.name || "";
    };
    const waypoints = addressed.map(toWaypoint).map(encodeURIComponent).filter(Boolean);
    if (!waypoints.length) {
      toast.error("Endereços insuficientes para gerar rota");
      return;
    }
    // /maps/dir/Origin/Waypoint1/…/Destination — travelmode=driving
    const url = `https://www.google.com/maps/dir/${waypoints.join("/")}/data=!4m2!4m1!3e0`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`Rota aberta no Google Maps · ${waypoints.length} paradas`);
  };

  const doExport = async (fmt, layout = "executive") => {
    const busyKey = layout === "visit_guide" ? "visit_guide" : fmt;
    setBusy(busyKey); setOpen(false);
    try {
      const params = new URLSearchParams({ fmt, limit: "200" });
      if (runId) params.set("run_id", runId);
      if (layout && layout !== "executive") params.set("layout", layout);
      const r = await axios.get(
        `${API}/ai-search/companies/export?${params.toString()}`,
        { responseType: "blob", timeout: 120000 });
      // Server sends Content-Disposition — respect it when available.
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1]
        : `smith-ai-search${layout === "visit_guide" ? "-visit-guide" : ""}.${fmt}`;
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href; a.download = fname; document.body.appendChild(a);
      a.click(); a.remove(); URL.revokeObjectURL(href);
      toast.success(layout === "visit_guide"
        ? "Guia de Visita gerado — download iniciado"
        : `${fmt.toUpperCase()} pronto — download iniciado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Falha no export ${fmt}`);
    } finally { setBusy(null); }
  };

  const items = [
    { fmt: "xlsx", label: "Excel (.xlsx)",   icon: FileSpreadsheet, hint: "Companies + Evidence + Summary" },
    { fmt: "csv",  label: "CSV (.csv)",      icon: FileSpreadsheet, hint: "Companies only" },
    { fmt: "pdf",  label: "PDF Report",       icon: FileText,        hint: "Executive summary" },
    { fmt: "docx", label: "Word (.docx)",    icon: FileType,        hint: "Qualification dossier" },
  ];

  return (
    <div className="relative flex items-center gap-2" data-testid="ai-search-export-bar">
      {/* Iter226 · Guia de Visita — always visible, one-click print PDF */}
      <button type="button"
        onClick={() => !disabled && doExport("pdf", "visit_guide")}
        disabled={disabled || !!busy}
        data-testid="ai-search-print-visit-guide-btn"
        title="Gerar guia de visita otimizado para impressão (paisagem, tabela limpa, coluna de anotações)"
        className="h-8 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40"
        style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
        {busy === "visit_guide"
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <FileText className="w-3.5 h-3.5" />}
        Imprimir Guia de Visita
      </button>

      {/* Iter227 · Rota Google Maps — abre até 10 paradas otimizadas */}
      <button type="button"
        onClick={() => !disabled && buildMapsRoute()}
        disabled={disabled}
        data-testid="ai-search-maps-route-btn"
        title="Abrir rota otimizada no Google Maps (até 10 paradas)"
        className="h-8 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40"
        style={{ background: "linear-gradient(135deg,#10B981 0%,#047857 100%)" }}>
        <MapPin className="w-3.5 h-3.5" />
        Rota Maps
      </button>

      <div className="relative">
        <button type="button"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
          data-testid="ai-search-export-btn"
          className="h-8 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-40"
          style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
          {busy && busy !== "visit_guide"
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Download className="w-3.5 h-3.5" />}
          Exportar
          <ChevronDown className="w-3 h-3 opacity-70" />
        </button>
        {open && (
          <div data-testid="ai-search-export-menu"
               className="absolute right-0 top-full mt-1 z-40 w-[260px] rounded-lg border border-sky-500/40 py-1 shadow-lg"
               style={{ background: "#0F172A" }}
               onMouseLeave={() => setOpen(false)}>
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <button key={it.fmt} type="button" onClick={() => doExport(it.fmt)}
                  data-testid={`ai-search-export-${it.fmt}`}
                  className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-white/5">
                  <Icon className="w-3.5 h-3.5 text-sky-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11.5px] font-bold text-white">{it.label}</div>
                    <div className="text-[9.5px] text-slate-400">{it.hint}</div>
                  </div>
                </button>
              );
            })}
            <div className="border-t border-white/5 my-1" />
            <button type="button" onClick={() => doExport("pdf", "visit_guide")}
              data-testid="ai-search-export-visit-guide"
              className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-white/5">
              <FileText className="w-3.5 h-3.5 text-red-300 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[11.5px] font-bold text-white">Guia de Visita (impressão)</div>
                <div className="text-[9.5px] text-slate-400">
                  Paisagem · sem poluição · coluna de anotações
                </div>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function AutoSendModal({ onClose, authHeader }) {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [scoreT,  setScoreT]  = useState(95);
  const [confT,   setConfT]   = useState(85);
  const [maxDay,  setMaxDay]  = useState(20);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/smith/outreach/auto-send-settings`, authHeader);
        setEnabled(!!r.data?.enabled);
        setScoreT(r.data?.score_threshold ?? 95);
        setConfT(r.data?.confidence_threshold ?? 85);
        setMaxDay(r.data?.max_per_day ?? 20);
      } catch { /* ignore */ }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/smith/outreach/auto-send-settings`,
        { enabled, score_threshold: scoreT, confidence_threshold: confT, max_per_day: maxDay },
        authHeader);
      toast.success(enabled ? "Auto-Send ativado" : "Auto-Send desativado");
      onClose();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
         onClick={onClose} data-testid="ai-search-autosend-modal">
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative w-[520px] max-w-[92vw] rounded-2xl border border-amber-500/25 p-5 space-y-4"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
              <Send className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-[14px] font-bold text-white">Auto-Send Outreach</h3>
              <p className="text-[10.5px] text-slate-400">
                Envio direto quando Signal Score & Confidence estão altos
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="ai-search-autosend-close"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {loading ? (
          <div className="py-6 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
              <div>
                <div className="text-[12.5px] font-bold text-white">Ativar Auto-Send</div>
                <div className="text-[10.5px] text-slate-400">
                  Envia direto (bypassa Drafts) quando as duas metas abaixo forem atingidas
                </div>
              </div>
              <button type="button" onClick={() => setEnabled(!enabled)}
                data-testid="ai-search-autosend-toggle"
                className={`w-11 h-6 rounded-full border transition-colors ${
                  enabled ? "bg-emerald-500/40 border-emerald-500" : "bg-slate-800 border-white/20"}`}>
                <span className={`block w-4 h-4 rounded-full bg-white transition-transform ${
                  enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>

            <Slider label="Signal Score threshold" value={scoreT}
              setValue={setScoreT} min={50} max={100}
              testid="ai-search-autosend-score" suffix=" pts" />
            <Slider label="Claire Confidence threshold" value={confT}
              setValue={setConfT} min={0} max={100}
              testid="ai-search-autosend-conf" suffix=" %" />
            <Slider label="Max envios / 24h" value={maxDay}
              setValue={setMaxDay} min={1} max={200}
              testid="ai-search-autosend-max" suffix=" emails" />

            <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-[11px] text-amber-200 flex gap-2">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <strong>Como funciona:</strong> quando um Buying Signal chegar com
                score ≥ <b>{scoreT}</b> e Claire calcular confiança ≥ <b>{confT}%</b>,
                o email é enviado direto via Resend — bypassa a fila de rascunhos.
                Blacklist e cap diário são respeitados.
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
              <button type="button" onClick={onClose}
                data-testid="ai-search-autosend-cancel"
                className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-slate-300 hover:text-white">
                Cancelar
              </button>
              <button type="button" onClick={save} disabled={saving}
                data-testid="ai-search-autosend-save"
                className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Salvar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Slider({ label, value, setValue, min, max, suffix, testid }) {
  return (
    <div data-testid={`${testid}-container`}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11.5px] font-bold text-slate-300">{label}</label>
        <span className="text-[12px] tabular-nums font-bold text-amber-300"
              data-testid={`${testid}-value`}>
          {value}{suffix}
        </span>
      </div>
      <input type="range" min={min} max={max} step="1" value={value}
        onChange={(e) => setValue(parseInt(e.target.value, 10))}
        data-testid={`${testid}-slider`}
        className="w-full accent-amber-500" />
    </div>
  );
}
